import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { RefreshCw, Database, Shield, AlertTriangle, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import storage from '../utils/storage';
import { getBackendUrl } from '../utils/api';

const BACKEND_URL = getBackendUrl();

// Categories with their Greek names
const CATEGORY_NAMES = {
  // Daypart categories
  morning: 'Πρωινό (06:00-10:00)',
  daytime: 'Daytime (10:00-16:00)',
  afternoon: 'Απόγευμα (16:00-19:00)',
  evening: 'Βράδυ (19:00-24:00)',
  weekend: 'Σαββατοκύριακο',
  // Other categories
  guess_the_profession: 'Μάντεψε το Επάγγελμα',
  games_riddles: 'Παιχνίδια & Γρίφοι',
  strange_weird: 'Παράξενα & Περίεργα',
  weird_news: 'Weird News',
  guess_song_from_lyric: 'Μάντεψε το Τραγούδι',
  truth_or_myth: 'Αλήθεια ή Μύθος',
  yes_or_no: 'Ναι ή Όχι',
  would_you_rather: 'Τι θα Προτιμούσες',
  real_or_epic_lie: 'Αλήθεια ή Ψέμα'
};

// Category emojis
const CATEGORY_EMOJIS = {
  morning: '☀️',
  daytime: '🌤️',
  afternoon: '🌅',
  evening: '🌙',
  weekend: '🎉',
  guess_the_profession: '🎯',
  games_riddles: '🧩',
  strange_weird: '🌀',
  weird_news: '📰',
  guess_song_from_lyric: '🎵',
  truth_or_myth: '❓',
  yes_or_no: '✅',
  would_you_rather: '🤔',
  real_or_epic_lie: '🎭'
};

const DedupDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [exclusions, setExclusions] = useState({});
  const [loadingExclusions, setLoadingExclusions] = useState({});

  const getAuthHeaders = () => {
    const token = storage.getItem('token');
    return {
      headers: { Authorization: `Bearer ${token}` }
    };
  };

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/dedup/stats`, getAuthHeaders());
      setStats(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Σφάλμα φόρτωσης στατιστικών');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const fetchExclusions = async (category) => {
    setLoadingExclusions(prev => ({ ...prev, [category]: true }));
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/dedup/exclusions/${category}?limit=50`,
        getAuthHeaders()
      );
      setExclusions(prev => ({ ...prev, [category]: response.data.exclusions || [] }));
    } catch (err) {
      console.error(`Error fetching exclusions for ${category}:`, err);
    } finally {
      setLoadingExclusions(prev => ({ ...prev, [category]: false }));
    }
  };

  const toggleCategory = (category) => {
    const isExpanded = expandedCategories[category];
    setExpandedCategories(prev => ({ ...prev, [category]: !isExpanded }));
    
    if (!isExpanded && !exclusions[category]) {
      fetchExclusions(category);
    }
  };

  const runMigration = async () => {
    if (!window.confirm('Θέλετε να εκτελέσετε migration για τα τελευταία 150 ημέρες; Αυτό θα ενημερώσει τη βάση tracking.')) {
      return;
    }
    
    setMigrating(true);
    setMigrationResult(null);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/dedup/migrate?days_back=150`,
        {},
        getAuthHeaders()
      );
      setMigrationResult(response.data);
      // Refresh stats after migration
      fetchStats();
    } catch (err) {
      setError(err.response?.data?.detail || 'Σφάλμα κατά το migration');
    } finally {
      setMigrating(false);
    }
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-500" />
        <span className="ml-2 text-gray-600">Φόρτωση στατιστικών...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-8 h-8 text-purple-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Deduplication Dashboard</h2>
            <p className="text-sm text-gray-500">Παρακολούθηση & διαχείριση αποτροπής επαναλήψεων</p>
          </div>
        </div>
        <button
          onClick={fetchStats}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Ανανέωση
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Overview Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl p-5 text-white">
            <div className="flex items-center gap-3">
              <Database className="w-10 h-10 opacity-80" />
              <div>
                <p className="text-purple-100 text-sm">Συνολικά Tracked Records</p>
                <p className="text-3xl font-bold">{stats.total_tracked_records?.toLocaleString()}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-5 text-white">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-10 h-10 opacity-80" />
              <div>
                <p className="text-green-100 text-sm">Κατηγορίες Ενεργές</p>
                <p className="text-3xl font-bold">{Object.keys(stats.category_stats || {}).length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-5 text-white">
            <div className="flex items-center gap-3">
              <Shield className="w-10 h-10 opacity-80" />
              <div>
                <p className="text-amber-100 text-sm">Προστασία</p>
                <p className="text-3xl font-bold">150 μέρες</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Migration Tool */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
        <h3 className="font-semibold text-blue-800 mb-2">🔄 Migration Tool</h3>
        <p className="text-blue-600 text-sm mb-3">
          Εκτελέστε migration για να συγχρονίσετε τα υπάρχοντα δεδομένα με το σύστημα tracking.
        </p>
        <div className="flex items-center gap-4">
          <button
            onClick={runMigration}
            disabled={migrating}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {migrating ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Εκτελείται...
              </>
            ) : (
              <>
                <Database className="w-4 h-4" />
                Εκτέλεση Migration (150 ημέρες)
              </>
            )}
          </button>
          
          {migrationResult && (
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle className="w-5 h-5" />
              <span>Migrated: {migrationResult.migrated} | Errors: {migrationResult.errors}</span>
            </div>
          )}
        </div>
      </div>

      {/* Category Details */}
      {stats?.category_stats && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Κατηγορίες & Αποκλεισμένα Θέματα</h3>
          
          {Object.entries(stats.category_stats).map(([category, data]) => (
            <div key={category} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleCategory(category)}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">
                    {CATEGORY_EMOJIS[category] || '📁'}
                  </span>
                  <div className="text-left">
                    <h4 className="font-semibold text-gray-800 dark:text-white">
                      {CATEGORY_NAMES[category] || category}
                    </h4>
                    <p className="text-sm text-gray-500">
                      {data.tracked_items} tracked items | {data.days_lookback} ημέρες lookback
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                    {data.tracked_items} items
                  </span>
                  {expandedCategories[category] ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </button>
              
              {expandedCategories[category] && (
                <div className="px-5 py-4 border-t border-gray-100 bg-gray-50">
                  <h5 className="text-sm font-medium text-gray-600 mb-3">
                    Αποκλεισμένα θέματα (δεν θα επαναχρησιμοποιηθούν):
                  </h5>
                  
                  {loadingExclusions[category] ? (
                    <div className="flex items-center gap-2 text-gray-500">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Φόρτωση...</span>
                    </div>
                  ) : exclusions[category]?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {exclusions[category].map((item, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm"
                        >
                          🚫 {item}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm italic">
                      Δεν υπάρχουν αποκλεισμένα θέματα ακόμα
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Info Box */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
        <h3 className="font-semibold text-amber-800 mb-2">ℹ️ Πώς λειτουργεί το Deduplication</h3>
        <ul className="text-amber-700 text-sm space-y-1 list-disc list-inside">
          <li><strong>Πριν τη δημιουργία:</strong> Το σύστημα ελέγχει ποια θέματα χρησιμοποιήθηκαν τις τελευταίες 150 μέρες</li>
          <li><strong>Αποκλεισμός:</strong> Η λίστα αποκλεισμένων θεμάτων περνάει στο AI για να μην τα επαναλάβει</li>
          <li><strong>Auto-retry:</strong> Αν παρόλα αυτά βρεθεί διπλότυπο, το σύστημα δοκιμάζει ξανά (μέχρι 3 φορές)</li>
          <li><strong>Tracking:</strong> Κάθε νέο θέμα καταγράφεται για μελλοντικό έλεγχο</li>
        </ul>
      </div>

      {/* Last Updated */}
      {stats?.timestamp && (
        <p className="text-center text-gray-400 text-sm">
          Τελευταία ενημέρωση: {new Date(stats.timestamp).toLocaleString('el-GR')}
        </p>
      )}
    </div>
  );
};

export default DedupDashboard;
